import React from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Field, Textarea, FluentProvider, webLightTheme } from '@fluentui/react-components';
import { useQuery } from '@apollo/client';
import { Controller, UseFormMethods } from 'react-hook-form';
import { IProcessCorpReasonForLateFiling } from '../Interfaces';
import IFieldsetProps from '../../../../utils/types/IFieldsetProps';
import GET_CORP_REASON_FOR_LATE_FILING_QUERY from '../../../../utils/statApi/CorpReasonForLateFilingApi';
import QueryBasedDropdown from '../../../common/formFields/queryBasedDropdown/QueryBasedDropdown';
import IDropdownReference from '../../../../utils/types/IDropdownReference';
import CanAccess from '../../../common/canAccess/CanAccess';

export interface ICorpReasonForLateFilingFormProps {
  processCorpReasonForLateFiling: IProcessCorpReasonForLateFiling;
  form: UseFormMethods<IProcessCorpReasonForLateFiling>;
}

const CorpReasonForLateFilingDropdown = (props: IFieldsetProps): JSX.Element => {
  const { value, label, handleChange, valueKey, outputKey, errors } = props;
  const { loading, data } = useQuery(GET_CORP_REASON_FOR_LATE_FILING_QUERY);
  const { corpReasonsForLateFiling } = !loading && data ? data : [];

  return (
    <>
      <QueryBasedDropdown
        id="corpReasonForLateFilingDropdown"
        value={value as IDropdownReference}
        label={label}
        outputKey={outputKey}
        dropdownOptionsData={corpReasonsForLateFiling}
        errors={errors}
        handleChange={handleChange}
        valueKey={valueKey}
        clearable
      />
    </>
  );
};

const CorpReasonForLateFilingForm = ({
  processCorpReasonForLateFiling,
  form,
}: ICorpReasonForLateFilingFormProps): JSX.Element => {
  const { control, errors, setValue } = form;

  const onReasonChange = (newValue: IDropdownReference, valueKey: string) => {
    setValue('corpReasonForLateFiling', newValue);
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 5 }}>
        <CanAccess requestedAction="CorpReasonForFilingViolation:Edit">
          <Controller
            control={control}
            name="corpReasonForLateFiling"
            value={processCorpReasonForLateFiling?.corpReasonForLateFiling}
            defaultValue={processCorpReasonForLateFiling?.corpReasonForLateFiling}
            render={({ value }) => (
              <CorpReasonForLateFilingDropdown
                label="Reason"
                valueKey="corpReasonForLateFiling"
                value={value}
                errors={errors}
                handleChange={onReasonChange}
              />
            )}
          />
        </CanAccess>
        <CanAccess requestedAction="CorpReasonForFilingViolation:Edit">
          <Controller
            control={control}
            name="corpReasonForLateFilingComment"
            defaultValue={processCorpReasonForLateFiling?.corpReasonForLateFilingComment || ''}
            rules={{
              maxLength: {
                value: 500,
                message: 'Comment cannot be longer than 500 characters',
              },
            }}
            render={({ onChange, onBlur, value, name, ref }) => (
              <Field
                label="Comments"
                validationMessage={errors?.corpReasonForLateFilingComment?.message}
              >
                <Textarea
                  id="corpReasonForLateFilingComment"
                  maxLength={500}
                  value={value}
                  name={name}
                  ref={ref}
                  onBlur={onBlur}
                  onChange={(ev, textareaData) => onChange(textareaData.value)}
                />
              </Field>
            )}
          />
        </CanAccess>
      </Stack>
    </FluentProvider>
  );
};

export default CorpReasonForLateFilingForm;
